import { File } from "@-/com.npmjs.expo-file-system";
import { Linking } from "@-/com.npmjs.react-native";
import { resolveHanWorkerUrl } from "@-/dev.improve.haniverse.lib.worker";
import { logger } from "@-/dev.improve.haniverse.lib.logger";
import * as z from "@-/com.npmjs.zod";

export interface OpenProfileResult {
  /** The `GET /profile/:id` URL that serves the raw `.cpuprofile` back. */
  profileUrl: string;
  /** The SpeedScope URL (`#profileURL=…`) that was opened in the browser. */
  speedscopeUrl: string;
}

// Shape returned by the worker's `POST /profile` route (apps/haniverse-worker).
const profileUploadResponseSchema = z.object({
  id: z.string(),
  profileUrl: z.string(),
  speedscopeUrl: z.string().min(1),
});

// Strip a trailing-slash run off the worker base URL before appending the path.
const TRAILING_SLASHES_RE = /\/+$/;

/**
 * Take a Hermes `.cpuprofile` saved on the device by
 * `react-native-release-profiler`'s `stopProfiling(true)`, upload it to the
 * `haniverse-worker` (`POST /profile`), and open the returned SpeedScope URL in the
 * device browser.
 *
 * This is the "open the profile in a browser from the device, without
 * `--fromDownload` / a cable" path: the worker stores the trace in R2 and hands
 * back a public URL that SpeedScope loads via its `#profileURL=` fragment. Works
 * on a TestFlight staging build — the only requirement is that the worker
 * (`EXPO_PUBLIC_HANIVERSE_WORKER_URL`, staging = `https://worker.staging.haniverse.app`)
 * has the `/profile` route deployed.
 *
 * Returns the URLs so the caller can surface / copy them; throws on any failure
 * (missing file, upload rejected, no browser) so the caller can show an error.
 */
export async function openProfileInBrowser(profilePath: string): Promise<OpenProfileResult> {
  const file = new File(profilePath);
  if (!file.exists) {
    throw new Error(`Profile file not found at ${profilePath}`);
  }

  const bytes = await file.bytes();
  if (bytes.byteLength === 0) {
    throw new Error("Profile file is empty — did the profiling session capture any samples?");
  }

  const workerBaseUrl = resolveHanWorkerUrl().replace(TRAILING_SLASHES_RE, "");
  const uploadUrl = `${workerBaseUrl}/profile`;

  logger(import.meta.location).log(
    `[release-profiler] uploading ${bytes.byteLength} byte profile → ${uploadUrl}`,
  );

  const response = await fetch(uploadUrl, {
    body: bytes,
    headers: { "Content-Type": "application/json" },
    method: "POST",
  });

  if (!response.ok) {
    const detail = await response.text().catch(() => "");
    throw new Error(
      `Profile upload failed (${response.status})${detail ? `: ${detail.slice(0, 200)}` : ""}`,
    );
  }

  const result = profileUploadResponseSchema.parse(await response.json());

  await Linking.openURL(result.speedscopeUrl);

  return { profileUrl: result.profileUrl, speedscopeUrl: result.speedscopeUrl };
}
